import { useEffect, useMemo, useState } from "react";
import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomTable from "../../../components/common/CustomTable";
import { useDispatch, useSelector } from "react-redux";
import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { useBoolean } from "../../../Hooks/useBoolean";
import {
   monthlyLoadingGraphMasterAsync,
   monthlyLoadingGraphBarMasterAsync,
} from "../../../redux/features/report-master/monthlyLoadingGraphSlice";
import CustomDropdown from "../../../components/common/CustomDropdown/CustomDropdown";
import CustomDatePicker from "../../../components/common/CustomDatePicker/CustomDatePicker";
import { reportDropDownRecordAsync } from "../../../redux/features/report-master/reportDropDownRecordSlice";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { BsFillEyeFill } from "react-icons/bs";
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   BarElement,
   Title,
   Tooltip,
   Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function MonthlyLoadingReport() {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [records, setRecords] = useState([]);

   const reportMasterList = useSelector(
      (state) => state.monthlyLoadingGraphSlice?.tripDetails,
   );
   console.log("reportData", reportMasterList);
   const monthWiseReport = useSelector(
      (state) => state.monthlyLoadingGraphSlice?.monthWise,
   );
   console.log("monthWiseReport", monthWiseReport);

   const columns = [
      {
         name: "Company Name",
         selector: (row) => row?.company_name,
         sortable: true,
      },
      {
         name: "Customer Name",
         selector: (row) => row?.customer_name,
         sortable: true,
      },

      {
         name: "Attach Location",
         selector: (row) => row?.attached_location,
         sortable: true,
      },

      {
         name: "Number Of Trips",
         selector: (row) => row?.number_of_trips,
         sortable: true,
      },

      {
         name: "Shortage",
         selector: (row) => row?.shortage,
         sortable: true,
      },
      {
         name: "Action",
         selector: (row) => (
            <button
               className="btn"
               onClick={() => navigate("/trip/contractual/edit/" + row.tripid)}
            >
               <BsFillEyeFill
                  className="text-primary"
                  style={{ height: "1.5rem", width: "1.5rem" }}
               />
            </button>
         ),
         width: "120px",
         center: true,
      },
   ];

   /// fetching filter drop down values
   const dropDownMaster = useSelector((state) => state.reportDropDownRecordSlice);

   const companyData = [
      { label: "Select..", value: "" },
      ...(dropDownMaster?.data?.company_list?.map?.((item) => ({
         label: item?.company_name,
         value: item?.id,
      })) || []),
   ];

   const customerData = [
      { label: "Select..", value: "" },
      ...(dropDownMaster?.data?.customer_list?.map?.((item) => ({
         label: item?.name,
         value: item?.id,
      })) || []),
   ];

   const formik = useFormik({
      initialValues: {
         company: "",
         customer: "",
         fromDate: "",
         toDate: "",
      },
   });

   const {
      errors,
      values,
      touched,
      getFieldProps,
      handleSubmit,
      setFieldValue,
      resetForm,
   } = formik;

   const reportPopup = useBoolean();

   const handleResetFilters = () => {
      resetForm();
   };

   const monthWiseSeries = useMemo(() => {
      if (!monthWiseReport) return [];

      if (Array.isArray(monthWiseReport)) {
         return monthWiseReport.map((item, idx) => ({
            label: item?.month_name || item?.month || item?.label || `Month ${idx + 1}`,
            trips: Number(item?.number_of_trips ?? item?.trips ?? item?.count ?? 0),
         }));
      }

      if (typeof monthWiseReport === "object") {
         return Object.entries(monthWiseReport).map(([key, value]) => ({
            label: key,
            trips:
               typeof value === "number"
                  ? value
                  : Number(value?.number_of_trips ?? value?.trips ?? value?.count ?? 0),
         }));
      }

      return [];
   }, [monthWiseReport]);

   const chartData = useMemo(() => {
      let labels = [];
      let trips = [];

      if (reportMasterList && Object.keys(reportMasterList).length > 0) {
         const tripsPerCompany = {};
         Object.values(reportMasterList).forEach((item) => {
            const company = item?.company_name || "Unknown";
            tripsPerCompany[company] =
               (tripsPerCompany[company] || 0) + Number(item?.number_of_trips || 0);
         });
         labels = Object.keys(tripsPerCompany);
         trips = Object.values(tripsPerCompany);
      } else if (monthWiseSeries.length > 0) {
         labels = monthWiseSeries.map((item) => item.label || "-");
         trips = monthWiseSeries.map((item) => item.trips || 0);
      }

      return {
         labels,
         datasets: [
            {
               label: "Number of Trips",
               data: trips,
               backgroundColor: "rgba(54, 162, 235, 0.6)",
               borderColor: "rgba(54, 162, 235, 1)",
               borderWidth: 1,
               borderRadius: 4,
            },
         ],
      };
   }, [monthWiseSeries, reportMasterList]);

   const chartOptions = useMemo(
      () => ({
         responsive: true,
         maintainAspectRatio: false,
         plugins: {
            legend: {
               position: "top",
            },
            title: {
               display: true,
               text: "Monthly Trips Overview",
            },
         },
         scales: {
            y: {
               beginAtZero: true,
               ticks: {
                  precision: 0,
               },
            },
            x: {
               ticks: {
                  callback: function (value) {
                     const label = this.getLabelForValue(value);
                     return label.length > 30 ? `${label.slice(0, 27)}...` : label;
                  },
               },
            },
         },
      }),
      [],
   );

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      const filter_params = {};
      if (values.company) filter_params.company_id = values.company;
      if (values.customer) filter_params.customer_id = values.customer;
      if (values.fromDate) filter_params.from_date = values.fromDate;
      if (values.toDate) filter_params.to_date = values.toDate;

      dispatch(
         monthlyLoadingGraphMasterAsync({
            data: filter_params,
            done: () => {
               dispatch(backdropLoadingAction(false));
            },
            err: (error) => {
               dispatch(backdropLoadingAction(false));
               //toast.error(error);
            },
         }),
      );

      dispatch(
         monthlyLoadingGraphBarMasterAsync({
            data: filter_params,
            err: (error) => {
               //toast.error(error);
            },
         }),
      );
   }, [values.company, values.customer, values.fromDate, values.toDate]);

   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      dispatch(
         reportDropDownRecordAsync({
            done: () => {
               dispatch(backdropLoadingAction(false));
            },
            err: (error) => {
               dispatch(backdropLoadingAction(false));
               //toast.error(error);
            },
         }),
      );
   }, []);

   useEffect(() => {
      if (Array.isArray(reportMasterList) && reportMasterList.length) {
         const transformedRecords = [...reportMasterList];
         const sortedRecords = transformedRecords.sort((a, b) => {
            let comp = a.company_name?.localeCompare(b.company_name);
            if (comp !== 0) return comp;

            comp = a.vehicle_no?.localeCompare(b.vehicle_no);
            if (comp !== 0) return comp;

            comp = a.customer?.localeCompare(b.customer);
            if (comp !== 0) return comp;

            return 0; // if all same
         });

         setRecords(sortedRecords);
      } else if (Array.isArray(reportMasterList) && !reportMasterList.length) {
         setRecords([]);
      }
   }, [reportMasterList]);

   /*const handleDownloadReport = (data) => {
      dispatch(backdropLoadingAction(true));

      const filter_params = { data };

      if (values.company) filter_params.company_id = values.company;

      dispatch(
         monthlyLoadingGraphMasterAsync({
            data: filter_params,
            err: () => {
               dispatch(backdropLoadingAction(false));
            },

            done: (report_list) => {
               const headers = [
                  "S. No.",
                  "Company Name",
                  "Attached Location",
                  "Number Of Trips",
                  "Shortage",
               ];

               let list = Object.values(report_list);

               list.sort((a, b) => {
                  let comp = (a.company_name ?? "").localeCompare(b.company_name ?? "");
                  if (comp !== 0) return comp;

                  // No vehicle_no or customer fields in this report
                  return 0;
               });

               const emiArr = [];

               list.forEach((item, index) => {
                  emiArr.push([
                     index + 1,
                     item?.company_name ?? "-",
                     item?.attached_location ?? "-",
                     item?.number_of_trips ?? "-",
                     item?.shortage ?? "-",
                  ]);
               });

               const sheetData = [headers, ...emiArr];

               const ws = XLSX.utils.aoa_to_sheet(sheetData);
               const wb = XLSX.utils.book_new();
               XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
               XLSX.writeFile(wb, "MONTHLY LOADING GRAPH.xlsx");

               dispatch(backdropLoadingAction(false));
               reportPopup.onFalse();
            },
         })
      );
   };
*/

   const handleDownloadReport = (data) => {
      dispatch(backdropLoadingAction(true));

      const filter_params = { data };

      if (values.company) filter_params.company_id = values.company;
      if (values.customer) filter_params.customer_id = values.customer;
      if (values.fromDate) filter_params.from_date = values.fromDate;
      if (values.toDate) filter_params.to_date = values.toDate;

      dispatch(
         monthlyLoadingGraphMasterAsync({
            data: filter_params,
            err: () => {
               dispatch(backdropLoadingAction(false));
            },

            done: (report_list) => {
               const headers = [
                  "S. No.",
                  "Company Name",
                  "Attached Location",
                  "Number Of Trips",
                  "Shortage",
               ];

               let list = Object.values(report_list);

               // Sort first
               list.sort((a, b) => {
                  let comp = (a.company_name ?? "").localeCompare(b.company_name ?? "");
                  if (comp !== 0) return comp;

                  comp = (a.attached_location ?? "").localeCompare(
                     b.attached_location ?? "",
                  );
                  if (comp !== 0) return comp;

                  return 0;
               });

               // === GROUP KEY (default company_name) ===
               const groupKey = "company_name";

               const groups = {};

               // Build groups
               list.forEach((item) => {
                  const key = item[groupKey] || "Unknown";

                  if (!groups[key]) {
                     groups[key] = {
                        items: [],
                        subtotal: {
                           trips: 0,
                           shortage: 0,
                        },
                     };
                  }

                  const g = groups[key];

                  const trips = parseFloat(item.number_of_trips) || 0;
                  const shortage = parseFloat(item.shortage) || 0;

                  g.items.push(item);

                  g.subtotal.trips += trips;
                  g.subtotal.shortage += shortage;
               });

               const finalRows = [];

               // === GRAND TOTAL VARIABLES ===
               let grand = {
                  trips: 0,
                  shortage: 0,
               };

               // Convert groups to Excel rows
               Object.keys(groups).forEach((groupName) => {
                  const g = groups[groupName];

                  g.items.forEach((item, index) => {
                     finalRows.push([
                        index + 1,
                        item.company_name ?? "-",
                        item.attached_location ?? "-",
                        item.number_of_trips ?? "-",
                        item.shortage ?? "-",
                     ]);
                  });

                  // Subtotal row
                  finalRows.push([
                     "",
                     "",
                     "Subtotal",
                     g.subtotal.trips.toFixed(0),
                     g.subtotal.shortage.toFixed(2),
                  ]);

                  // Add to Grand Total
                  grand.trips += g.subtotal.trips;
                  grand.shortage += g.subtotal.shortage;

                  // Blank Row
                  finalRows.push(["", "", "", "", ""]);
               });

               // GRAND TOTAL ROW
               finalRows.push([
                  "",
                  "",
                  "GRAND TOTAL",
                  grand.trips.toFixed(0),
                  grand.shortage.toFixed(2),
               ]);

               const sheetData = [headers, ...finalRows];

               const ws = XLSX.utils.aoa_to_sheet(sheetData);
               const wb = XLSX.utils.book_new();
               XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

               XLSX.writeFile(wb, "MONTHLY_LOADING_GRAPH.xlsx");

               dispatch(backdropLoadingAction(false));
               reportPopup.onFalse();
            },
         }),
      );
   };

   return (
      <div>
         <BodyHeader title="Monthly Loading Graph" />
         <div className="px-3">
            <div className="row align-items-center">
               <div className="col-lg-2">
                  <CustomDropdown
                     label="Company"
                     optionData={companyData}
                     value={
                        companyData.find((option) => option.value === values.company) ||
                        companyData[0]
                     }
                     onChange={(value) => {
                        const val = Array.isArray(value) ? value[0]?.value : value?.value;
                        setFieldValue("company", val || "");
                     }}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomDropdown
                     label="Customer"
                     optionData={customerData}
                     value={
                        customerData.find((option) => option.value === values.customer) ||
                        customerData[0]
                     }
                     onChange={(value) => {
                        const val = Array.isArray(value) ? value[0]?.value : value?.value;
                        setFieldValue("customer", val || "");
                     }}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomDatePicker
                     label="From Date"
                     value={values.fromDate}
                     onChange={(event) => setFieldValue("fromDate", event.target.value)}
                  />
               </div>
               <div className="col-lg-2">
                  <CustomDatePicker
                     label="To Date"
                     value={values.toDate}
                     onChange={(event) => setFieldValue("toDate", event.target.value)}
                  />
               </div>
               <div className="col-lg-1 me-2"></div>
               <div className="col-lg-3 pt-2 mt-2 d-flex justify-content-end gap-2">
                  <button
                     type="button"
                     className="btn btn-outline-secondary"
                     onClick={handleResetFilters}
                  >
                     Reset Filters
                  </button>

                  {/* <DownloadReportButton
                     title="Download Report"
                     onDownloadReport={handleDownloadReport}
                     confirmPopup={reportPopup}
                  /> */}
               </div>
            </div>
            <div className="row mt-4">
               <div className="col-12">
                  <div className="card">
                     <div className="card-header">
                        <h5 className="mb-0">Trips Overview</h5>
                     </div>
                     <div className="card-body" style={{ height: "400px" }}>
                        {chartData.labels.length ? (
                           <Bar options={chartOptions} data={chartData} />
                        ) : (
                           <p className="text-center text-muted mb-0">
                              No data available to visualize.
                           </p>
                        )}
                     </div>
                  </div>
               </div>
            </div>
            {/* <CustomTable columns={columns} data={records} /> */}
         </div>
      </div>
   );
}
